import { useQueryApi } from 'utils/api'
import { apiPaths } from 'api/kinerja/kepala-sekolah/config'

import type {
  TRiwayatDetailResponse,
  TRiwayatHasilEvaluasiResponse,
  TRiwayatHasilEvaluasiTahunanResponse,
  TRiwayatId,
  TRiwayatItemDetail,
  TRiwayatPayload,
  TRiwayatPelaksanaanPengembanganKompetensiResponse,
  TRiwayatPelaksanaanPerilakuKerjaKontenResponse,
  TRiwayatPelaksanaanPerilakuKerjaResponse,
  TRiwayatPelaksanaanPraktikKinerjaResponse,
  TRiwayatPerencanaanResponse,
  TRiwayatResponse,
  TRiwayatTahunanResponse,
} from 'types/kinerja-riwayat-ks'
import type { UseQueryOptions } from 'react-query'

export const API_GET_KS_RIWAYAT = 'API_GET_KS_RIWAYAT'
export const API_GET_KS_RIWAYAT_DETAIL = 'API_GET_KS_RIWAYAT_DETAIL'
export const API_GET_KS_RIWAYAT_HASIL_EVALUASI =
  'API_GET_KS_RIWAYAT_HASIL_EVALUASI'
export const API_GET_KS_RIWAYAT_HASIL_EVALUASI_TAHUNAN =
  'API_GET_KS_RIWAYAT_HASIL_EVALUASI_TAHUNAN'
export const API_GET_KS_RIWAYAT_HASIL_EVALUASI_TAHUNAN_DETAIL =
  'API_GET_KS_RIWAYAT_HASIL_EVALUASI_TAHUNAN_DETAIL'
export const API_GET_KS_RIWAYAT_PERENCANAAN = 'API_GET_KS_RIWAYAT_PERENCANAAN'
export const API_GET_KS_RIWAYAT_PELAKSANAAN_PRAKTIK_KINERJA =
  'API_GET_KS_RIWAYAT_PELAKSANAAN_PRAKTIK_KINERJA'
export const API_GET_KS_RIWAYAT_PELAKSANAAN_PENGEMBANGAN_KOMPETENSI =
  'API_GET_KS_RIWAYAT_PELAKSANAAN_PENGEMBANGAN_KOMPETENSI'
export const API_GET_KS_RIWAYAT_PELAKSANAAN_PERILAKU_KERJA =
  'API_GET_KS_RIWAYAT_PELAKSANAAN_PERILAKU_KERJA'
export const API_GET_KS_RIWAYAT_PELAKSANAAN_PERILAKU_KERJA_ALL =
  'API_GET_KS_RIWAYAT_PELAKSANAAN_PERILAKU_KERJA_ALL'

// Riwayat: Get list rincian per periode
export function useApiGetRiwayatByYear(
  props: TRiwayatPayload,
  opts?: UseQueryOptions<TRiwayatResponse>
) {
  const yearParam = props?.year || undefined
  const rolesParam = props?.roles || undefined
  const npsnParam = props?.npsn || undefined

  const yearParamParsed = parseInt(`${yearParam}`, 10)

  const queryKey = [
    API_GET_KS_RIWAYAT,
    yearParam ? yearParamParsed : undefined,
    ...(rolesParam ? [rolesParam?.join(',')] : []),
    npsnParam,
  ]

  return useQueryApi<TRiwayatResponse>(apiPaths.riwayat, {
    queryKey,
    axiosConfig: {
      params: { year: yearParamParsed, roles: rolesParam, npsn: npsnParam },
    },
    queryConfig: { useErrorBoundary: false, ...opts },
  })
}

// Riwayat: Get Riwayat Detail by Riwayat ID
export function useApiGetRiwayatDetail(
  id: TRiwayatId,
  opts?: UseQueryOptions<TRiwayatDetailResponse>
) {
  const queryKey = [API_GET_KS_RIWAYAT_DETAIL, id]

  return useQueryApi<TRiwayatDetailResponse>(`${apiPaths.riwayat}/detail`, {
    queryKey,
    axiosConfig: { params: { id } },
    queryConfig: { useErrorBoundary: false, ...opts },
  })
}

// Riwayat: Get Hasil Evaluasi by Riwayat ID
export function useApiGetRiwayatHasilEvaluasi(
  id: TRiwayatId,
  opts?: UseQueryOptions<TRiwayatHasilEvaluasiResponse>
) {
  const queryKey = [API_GET_KS_RIWAYAT_HASIL_EVALUASI, id]

  return useQueryApi<TRiwayatHasilEvaluasiResponse>(
    `${apiPaths.riwayat}/result`,
    {
      queryKey,
      axiosConfig: { params: { id } },
      queryConfig: { useErrorBoundary: false, ...opts },
    }
  )
}

// Riwayat: Get list rincian tahunan
export function useApiGetRiwayatTahunanByYear(
  props: TRiwayatPayload,
  opts?: UseQueryOptions<TRiwayatTahunanResponse>
) {
  const yearParam = props?.year || undefined
  const rolesParam = props?.roles || undefined
  const npsnParam = props?.npsn || undefined

  const yearParamParsed = parseInt(`${yearParam}`, 10)

  const queryKey = [
    API_GET_KS_RIWAYAT_HASIL_EVALUASI_TAHUNAN,
    yearParam ? yearParamParsed : undefined,
    ...(rolesParam ? [rolesParam?.join(',')] : []),
    npsnParam,
  ]

  return useQueryApi<TRiwayatTahunanResponse>(
    `${apiPaths.riwayat}/evaluasi-tahunan`,
    {
      queryKey,
      axiosConfig: {
        params: { year: yearParamParsed, roles: rolesParam, npsn: npsnParam },
      },
      queryConfig: { useErrorBoundary: false, ...opts },
    }
  )
}

// Riwayat: Get Hasil Evaluasi Tahunan by ID
export function useApiGetRiwayatHasilEvaluasiTahunan(
  id: TRiwayatId,
  opts?: UseQueryOptions<TRiwayatHasilEvaluasiTahunanResponse>
) {
  const queryKey = [API_GET_KS_RIWAYAT_HASIL_EVALUASI_TAHUNAN_DETAIL, id]

  return useQueryApi<TRiwayatHasilEvaluasiTahunanResponse>(
    `${apiPaths.riwayat}/evaluasi-tahunan/detail`,
    {
      queryKey,
      axiosConfig: { params: { id } },
      queryConfig: { useErrorBoundary: false, ...opts },
    }
  )
}

export function useApiGetRiwayatHasilEvaluasiTahunanGuru(
  id: TRiwayatId,
  opts?: UseQueryOptions<TRiwayatHasilEvaluasiTahunanResponse>
) {
  const queryKey = [API_GET_KS_RIWAYAT_HASIL_EVALUASI_TAHUNAN_DETAIL, id]

  return useQueryApi<TRiwayatHasilEvaluasiTahunanResponse>(
    `${apiPaths.riwayatGuru}/evaluasi-tahunan/detail`,
    {
      queryKey,
      axiosConfig: { params: { id } },
      queryConfig: { useErrorBoundary: false, ...opts },
    }
  )
}

// Riwayat: Get Perencanaan by SKP ID
export function useApiGetRiwayatPerencanaan(
  id: TRiwayatItemDetail['perencanaanSkpId'],
  opts?: UseQueryOptions<TRiwayatPerencanaanResponse>
) {
  const queryKey = [API_GET_KS_RIWAYAT_PERENCANAAN, id]

  return useQueryApi<TRiwayatPerencanaanResponse>(
    `${apiPaths.riwayat}/perencanaan`,
    {
      queryKey,
      axiosConfig: { params: { perencanaanSkpId: id } },
      queryConfig: { useErrorBoundary: false, ...opts },
    }
  )
}

// Riwayat: Get Pelaksanaan Kinerja - Praktik Kinerja by Pelaksanaan SKP ID
export function useApiGetRiwayatPelaksanaanPraktikKinerja(
  id: TRiwayatItemDetail['pelaksanaanSkpId'],
  opts?: UseQueryOptions<TRiwayatPelaksanaanPraktikKinerjaResponse>
) {
  const queryKey = [API_GET_KS_RIWAYAT_PELAKSANAAN_PRAKTIK_KINERJA, id]

  return useQueryApi<TRiwayatPelaksanaanPraktikKinerjaResponse>(
    `${apiPaths.riwayat}/pelaksanaan`,
    {
      queryKey,
      axiosConfig: { params: { pelaksanaanSkpId: id } },
      queryConfig: { useErrorBoundary: false, ...opts },
    }
  )
}

// Riwayat: Get Pelaksanaan Kinerja - Pengembangan Kompetensi by Perencanaan SKP ID
export function useApiGetRiwayatPelaksanaanPengembanganKompetensi(
  id: TRiwayatItemDetail['perencanaanSkpId'],
  opts?: UseQueryOptions<TRiwayatPelaksanaanPengembanganKompetensiResponse>
) {
  const queryKey = [API_GET_KS_RIWAYAT_PELAKSANAAN_PENGEMBANGAN_KOMPETENSI, id]

  return useQueryApi<TRiwayatPelaksanaanPengembanganKompetensiResponse>(
    `${apiPaths.riwayat}/bukti-dukung`,
    {
      queryKey,
      axiosConfig: { params: { perencanaanSkpId: id } },
      queryConfig: { useErrorBoundary: false, ...opts },
    }
  )
}

// Riwayat: Get Pelaksanaan Kinerja - Perilaku Kerja by Perencanaan SKP ID
export function useApiGetRiwayatPelaksanaanPerilakuKerja(
  id: TRiwayatItemDetail['perencanaanSkpId'],
  opts?: UseQueryOptions<TRiwayatPelaksanaanPerilakuKerjaResponse>
) {
  const queryKey = [API_GET_KS_RIWAYAT_PELAKSANAAN_PERILAKU_KERJA, id]

  return useQueryApi<TRiwayatPelaksanaanPerilakuKerjaResponse>(
    `${apiPaths.riwayat}/perilaku-kerja`,
    {
      queryKey,
      axiosConfig: { params: { perencanaanSkpId: id } },
      queryConfig: { useErrorBoundary: false, ...opts },
    }
  )
}

// Riwayat: Get Pelaksanaan Kinerja - Indikator dan Perujudan Perilaku Kerja
export function useApiGetRiwayatPelaksanaanPerilakuKerjaAll(
  id: TRiwayatId,
  opts?: UseQueryOptions<TRiwayatPelaksanaanPerilakuKerjaKontenResponse>
) {
  const queryKey = [API_GET_KS_RIWAYAT_PELAKSANAAN_PERILAKU_KERJA_ALL, id]

  return useQueryApi<TRiwayatPelaksanaanPerilakuKerjaKontenResponse>(
    `${apiPaths.riwayat}/perilaku-kerja/konten`,
    {
      queryKey,
      axiosConfig: { params: { perencanaanSkpId: id } },
      queryConfig: { useErrorBoundary: false, ...opts },
    }
  )
}
